// src/components/family/AddChildModal.tsx
// Modal dialog for adding a new child to a family

import { useEffect, useRef } from 'react';
import { X, UserPlus } from 'lucide-react';
import { useCreateChild } from '@/features/children/hooks/useChildren';
import { ChildForm } from './ChildForm';
import type { ChildFormData } from '@/features/children/types/childTypes';

interface AddChildModalProps {
  isOpen: boolean;
  onClose: () => void;
  familyId: string;
  onSuccess?: () => void;
}

export function AddChildModal({ isOpen, onClose, familyId, onSuccess }: AddChildModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const createChild = useCreateChild(familyId);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleTabKey);
      firstElement?.focus();
      
      return () => document.removeEventListener('keydown', handleTabKey);
    }
  }, [isOpen]);

  const handleSubmit = async (data: ChildFormData) => {
    try {
      await createChild.mutateAsync({
        first_name: data.first_name,
        last_name: data.last_name,
        nickname: data.nickname || undefined,
        date_of_birth: data.date_of_birth,
        gender: data.gender || undefined,
        birth_weight_oz: data.birth_weight_oz || undefined,
        birth_length_inches: data.birth_length_inches || undefined,
        gestational_age_weeks: data.gestational_age_weeks || undefined,
        birth_hospital: data.birth_hospital || undefined,
        birth_city: data.birth_city || undefined,
        birth_state: data.birth_state || undefined,
        delivery_type: data.delivery_type || undefined,
        blood_type: data.blood_type || undefined,
        allergies: data.allergies,
        insurance_provider: data.insurance_provider || undefined,
        insurance_policy_number: data.insurance_policy_number || undefined,
        medicaid_id: data.medicaid_id || undefined,
        notes: data.notes || undefined,
      });
      
      onSuccess?.();
      onClose();
    } catch (error) {
      // Error is handled by TanStack Query - could add toast notification here
      console.error('Failed to create child:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-navy/40 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-child-title"
        className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-lifted overflow-hidden animate-slideUp flex flex-col mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-warm-gray/10 bg-gradient-to-r from-sage/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sage/10 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-sage" />
            </div>
            <div>
              <h2 id="add-child-title" className="font-display font-semibold text-lg text-navy">
                Add Child
              </h2>
              <p className="text-sm text-warm-gray">Create a new child profile</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-cream text-warm-gray hover:text-navy transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <ChildForm
          onSubmit={handleSubmit}
          onCancel={onClose}
          isSubmitting={createChild.isPending}
          mode="add"
        />

        {/* Error Display */}
        {createChild.isError && (
          <div className="absolute bottom-20 left-6 right-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-700">
              {createChild.error instanceof Error
                ? createChild.error.message
                : 'Failed to add child. Please try again.'}
            </p>
          </div>
        )}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.98);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </div>
  );
}

export default AddChildModal;

